import React, { useEffect, useMemo, useState } from 'react';
import { Room, RoomBooking, Transaction, TransactionItem } from '../types';
import * as db from '../services/db';
import { usePos } from '../context/PosContext';
import './Rooms.css';

// Rooms / hotel module (Android LDProduct.rooms + Bill check-in/out fields:
// checkInTime/checkOutTime/duration/days/roomsDays). A room can be a master
// entry; checking in opens a booking, checking out computes nights × rent +
// extras and settles it as a sale through PosContext.recordSale.
const nightsBetween = (checkIn: string, checkOut: string) => {
  const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime();
  return Math.max(1, Math.ceil(ms / 86400000));
};

const Rooms: React.FC = () => {
  const { recordSale, customers, formatMoney } = usePos();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [bookings, setBookings] = useState<RoomBooking[]>([]);
  useEffect(() => {
    let alive = true;
    Promise.all([db.getRooms(), db.getRoomBookings()]).then(([r, b]) => {
      if (!alive) return;
      setRooms(r);
      setBookings(b);
    });
    return () => {
      alive = false;
    };
  }, []);

  // master form
  const [rName, setRName] = useState('');
  const [rType, setRType] = useState('');
  const [rRent, setRRent] = useState('');

  // active folio
  const [folioRoom, setFolioRoom] = useState<Room | null>(null);
  const [guest, setGuest] = useState('');
  const [guests, setGuests] = useState('1');
  const [exName, setExName] = useState('');
  const [exAmt, setExAmt] = useState('');

  const saveRooms = (next: Room[]) => {
    db.saveRooms(next);
    setRooms(next);
  };
  const saveBookings = (next: RoomBooking[]) => {
    db.saveRoomBookings(next);
    setBookings(next);
  };

  const openBookingFor = (roomId: string) => bookings.find((b) => b.roomId === roomId && b.status === 'open');

  const addRoom = () => {
    if (!rName.trim()) return alert('Room name is required.');
    saveRooms([
      ...rooms,
      { id: db.newId(), name: rName.trim(), type: rType.trim() || undefined, rentPerDay: parseFloat(rRent) || 0, status: 'available' },
    ]);
    setRName('');
    setRType('');
    setRRent('');
  };

  const removeRoom = (room: Room) => {
    if (openBookingFor(room.id)) return alert('Room is occupied — check out first.');
    if (window.confirm(`Delete room ${room.name}?`)) saveRooms(rooms.filter((r) => r.id !== room.id));
  };

  const checkIn = (room: Room) => {
    const booking: RoomBooking = {
      id: db.newId(),
      roomId: room.id,
      roomName: room.name,
      customerName: guest.trim() || undefined,
      guests: parseInt(guests, 10) || 1,
      checkIn: new Date().toISOString(),
      rentPerDay: room.rentPerDay,
      extras: [],
      status: 'open',
    };
    saveBookings([...bookings, booking]);
    saveRooms(rooms.map((r) => (r.id === room.id ? { ...r, status: 'occupied' } : r)));
    setGuest('');
    setGuests('1');
    setFolioRoom(null);
  };

  const folioBooking = folioRoom ? openBookingFor(folioRoom.id) : undefined;

  const addExtra = () => {
    if (!folioBooking || !exName.trim()) return;
    const amt = parseFloat(exAmt) || 0;
    saveBookings(
      bookings.map((b) =>
        b.id === folioBooking.id ? { ...b, extras: [...b.extras, { name: exName.trim(), amount: amt }] } : b
      )
    );
    setExName('');
    setExAmt('');
  };

  const folioTotals = useMemo(() => {
    if (!folioBooking) return null;
    const checkOut = new Date().toISOString();
    const nights = nightsBetween(folioBooking.checkIn, checkOut);
    const rent = nights * folioBooking.rentPerDay;
    const extras = folioBooking.extras.reduce((s, e) => s + e.amount, 0);
    return { checkOut, nights, rent, extras, total: rent + extras };
  }, [folioBooking]);

  const checkOut = async () => {
    if (!folioBooking || !folioTotals || !folioRoom) return;
    const items: TransactionItem[] = [
      {
        productId: `room-${folioRoom.id}`,
        name: `Room ${folioRoom.name} · ${folioTotals.nights} night(s)`,
        quantity: folioTotals.nights,
        unitPrice: folioBooking.rentPerDay,
        taxRatePercent: 0,
        total: folioTotals.rent,
      },
      ...folioBooking.extras.map((e) => ({
        productId: 'room-extra',
        name: e.name,
        quantity: 1,
        unitPrice: e.amount,
        taxRatePercent: 0,
        total: e.amount,
      })),
    ];
    const tx: Transaction = {
      id: Date.now().toString(),
      billNo: await db.peekNextBillNo(),
      customerName: folioBooking.customerName,
      covers: folioBooking.guests,
      items,
      subtotal: folioTotals.total,
      tax: 0,
      discount: 0,
      total: folioTotals.total,
      paymentMethod: 'cash',
      timestamp: new Date().toISOString(),
      status: 'completed',
    };
    const finalized = await recordSale(tx);
    saveBookings(
      bookings.map((b) =>
        b.id === folioBooking.id ? { ...b, status: 'closed', checkOut: folioTotals.checkOut, billNo: finalized.billNo } : b
      )
    );
    saveRooms(rooms.map((r) => (r.id === folioRoom.id ? { ...r, status: 'available' } : r)));
    setFolioRoom(null);
    alert(`Checked out. Bill ${finalized.billNo} · ${formatMoney(folioTotals.total)}`);
  };

  return (
    <div className="rooms-page">
      {/* room master */}
      <div className="rooms-master card-rounded">
        <h3>Add Room</h3>
        <input placeholder="Room name / no *" value={rName} onChange={(e) => setRName(e.target.value)} />
        <input placeholder="Type (Deluxe / AC …)" value={rType} onChange={(e) => setRType(e.target.value)} />
        <input placeholder="Rent / day" type="number" value={rRent} onChange={(e) => setRRent(e.target.value)} />
        <button className="rooms-btn" onClick={addRoom}>+ Add Room</button>
      </div>

      {/* floor grid */}
      <div className="rooms-floor card-rounded">
        <h3>Rooms</h3>
        {rooms.length === 0 && <p className="muted">No rooms yet.</p>}
        <div className="rooms-grid">
          {rooms.map((room) => {
            const b = openBookingFor(room.id);
            return (
              <div
                key={room.id}
                className={`room-tile ${room.status}`}
                onClick={() => setFolioRoom(room)}
                title={b ? `Occupied since ${new Date(b.checkIn).toLocaleString()}` : 'Available'}
              >
                <span className="room-name">{room.name}</span>
                {room.type && <span className="room-type">{room.type}</span>}
                <span className="room-rent">{formatMoney(room.rentPerDay)}/day</span>
                <span className="room-status">{room.status}</span>
                {room.status === 'available' && (
                  <button className="room-del" onClick={(e) => { e.stopPropagation(); removeRoom(room); }}>✕</button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* folio / check-in panel */}
      {folioRoom && (
        <div className="rooms-overlay" onClick={() => setFolioRoom(null)}>
          <div className="rooms-folio" onClick={(e) => e.stopPropagation()}>
            <div className="folio-head">
              Room {folioRoom.name}
              <button onClick={() => setFolioRoom(null)}>✕</button>
            </div>

            {!folioBooking ? (
              <div className="folio-body">
                <p className="muted">Available — check in a guest.</p>
                <input list="rooms-cust" placeholder="Guest name" value={guest} onChange={(e) => setGuest(e.target.value)} />
                <datalist id="rooms-cust">
                  {customers.map((c) => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
                <input placeholder="No. of guests" type="number" value={guests} onChange={(e) => setGuests(e.target.value)} />
                <button className="rooms-btn" onClick={() => checkIn(folioRoom)}>Check In</button>
              </div>
            ) : (
              <div className="folio-body">
                <div className="folio-row"><span>Guest</span><span>{folioBooking.customerName ?? '—'}</span></div>
                <div className="folio-row"><span>Check-in</span><span>{new Date(folioBooking.checkIn).toLocaleString()}</span></div>
                <div className="folio-row"><span>Nights</span><span>{folioTotals?.nights}</span></div>
                <div className="folio-row"><span>Room rent</span><span>{formatMoney(folioTotals?.rent ?? 0)}</span></div>

                <div className="pf-section">Extras (food / services)</div>
                {folioBooking.extras.map((e, i) => (
                  <div className="folio-row" key={i}><span>{e.name}</span><span>{formatMoney(e.amount)}</span></div>
                ))}
                <div className="folio-extra-add">
                  <input placeholder="Item" value={exName} onChange={(e) => setExName(e.target.value)} />
                  <input placeholder="Amt" type="number" value={exAmt} onChange={(e) => setExAmt(e.target.value)} />
                  <button onClick={addExtra}>+</button>
                </div>

                <div className="folio-row total"><span>Total</span><span>{formatMoney(folioTotals?.total ?? 0)}</span></div>
                <button className="rooms-btn checkout" onClick={checkOut}>Check Out & Settle</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Rooms;
